import { getRumClient } from '../../lib/rum/rumClient';
import { logRumError } from '../../lib/rum/rumErrorLogger';
import { getRumFeatureErrorMessage, RumFeature } from '../../lib/rum/rumFeatures';
import { AutoApplyAction } from './autoApplyActions';

/**
 * ## Frontend availability classification
 *
 * | Failure class                                           | `pageFailure`   |
 * | ------------------------------------------------------- | --------------- |
 * | Failure preventing required data or a core action       | `true`          |
 * | Tolerated fallback, optional data, or diagnostic result | Caller override |
 *
 * Keep each route's detailed classification in the page or hook that reports the error. RUM
 * context must remain bounded and must not include draft IDs, contact data, filenames, or URLs
 * with query parameters. The wrapper replaces source errors with sanitized, cause-free errors;
 * the host `beforeSend` hook reapplies message and stack-header redaction as defense in depth.
 */
export interface AutoApplyRumErrorContext {
    page: string;
    action: string;
    operation?: string;
    failureKind?: 'verification';
    failureCategory?: 'network' | 'application';
}

type QuestionsSubmitAction =
    | typeof AutoApplyAction.Update
    | typeof AutoApplyAction.ReviewApplication;

export const AutoApplyRumContext = {
    DraftValidation: { page: 'index', action: AutoApplyAction.ValidateDrafts },
    ReviewDraftLoad: { page: 'review', action: AutoApplyAction.LoadDraft },
    ReviewPreviewLoad: { page: 'review', action: AutoApplyAction.LoadPreview },
    ReviewPreviewValidation: {
        page: 'review',
        action: AutoApplyAction.LoadPreview,
        operation: 'validate-job',
    },
    ReviewPreviewCreation: {
        page: 'review',
        action: AutoApplyAction.LoadPreview,
        operation: AutoApplyAction.CreatePreview,
    },
    ReviewPreviewResponse: {
        page: 'review',
        action: AutoApplyAction.LoadPreview,
        operation: 'validate-preview',
        failureKind: 'verification',
    },
    ReviewPreviewDecode: {
        page: 'review',
        action: AutoApplyAction.LoadPreview,
        operation: 'decode-preview',
    },
    ContactInfoLoad: { page: 'contact-info', action: AutoApplyAction.Load },
    ContactInfoSave: {
        page: 'contact-info',
        action: AutoApplyAction.Update,
        operation: 'save',
    },
    ContactInfoItv: {
        page: 'contact-info',
        action: AutoApplyAction.Update,
        operation: 'save',
        failureKind: 'verification',
    },
    QuestionsLoad: { page: 'questions', action: AutoApplyAction.Load },
    QuestionsSave: (action: QuestionsSubmitAction) => ({
        page: 'questions',
        action,
        operation: 'save',
    }),
    QuestionsItv: (action: QuestionsSubmitAction) => ({
        page: 'questions',
        action,
        operation: 'save',
        failureKind: 'verification' as const,
    }),
    QuestionsFileUpload: { page: 'questions', action: AutoApplyAction.UploadFile },
    ConfirmationLoad: { page: 'confirmation', action: AutoApplyAction.Load },
    ConfirmationPreview: { page: 'confirmation', action: AutoApplyAction.CreatePreview },
    ConfirmationPreviewValidation: {
        page: 'confirmation',
        action: AutoApplyAction.CreatePreview,
        operation: 'validate-preview',
        failureKind: 'verification',
    },
    ConfirmationSubmit: {
        page: 'confirmation',
        action: AutoApplyAction.SubmitApplications,
        operation: 'submit',
    },
    ConfirmationSubmitResponse: {
        page: 'confirmation',
        action: AutoApplyAction.SubmitApplications,
        operation: 'submit',
        failureKind: 'verification',
    },
    SuccessLoad: { page: 'success', action: AutoApplyAction.Load },
    SuccessVerification: { page: 'success', action: AutoApplyAction.VerifySubmission },
} as const;

/**
 * Reports a sanitized copy of an Auto Apply error with stable journey metadata for RUM analysis.
 * Failures are page failures by default. Callers must explicitly opt out for tolerated outcomes.
 */
export const logAutoApplyRumError = (
    error: unknown,
    context: AutoApplyRumErrorContext,
    isPageFailure = true,
): void => {
    const rumContext = {
        feature: RumFeature.AutoApply,
        ...context,
    };
    logRumError(getRumClient(), error, {
        isPageFailure,
        context: rumContext,
        // Datadog's SDK does not allow beforeSend to modify error.causes. Replace the source
        // error before it enters the SDK so flattened cause chains cannot expose source data.
        sanitizedMessage: getRumFeatureErrorMessage(rumContext),
    });
};
